import { CSSProperties, memo } from 'react';

import { FRAME_TIERS } from 'components/Streaks/AvatarFrame';
import { FrameTier } from 'components/Streaks/model/types';

/**
 * The streak frame on a table seat (EN-11164), drawn as an overlay OUTSIDE
 * the seat's avatar circle in percentages of whatever box it sits in, so it
 * fits every seat size (48 to 62 px) and takes no layout space: the seat,
 * its name plate and the dealer button never move when it comes or goes.
 *
 * Static by design (the Legend glow and sparks are off at the table, and a
 * permanently visible loop on every seat is the worst idle-heat case: many
 * overlapping animated layers). Hidden while that seat's turn timer runs,
 * since the timer ring sits exactly where the band is.
 */

// Band = 8.5% of the avatar's diameter per side, so the frame box is 117% of
// the avatar and the band is 0.085 / 1.17 = 7.26% of the frame box.
const BAND = 0.085;
const BOX = 1 + 2 * BAND;
const BAND_OF_BOX = BAND / BOX;
const INSET = `${-BAND * 100}%`;
// The transparent hole, as a fraction of the frame's radius.
const HOLE = `${((1 - 2 * BAND_OF_BOX) * 100).toFixed(2)}%`;

const ring: CSSProperties = { position: 'absolute', inset: 0, borderRadius: '50%' };

function SeatFrame({ tier, hidden }: { tier: FrameTier | null; hidden?: boolean }) {
  if (!tier || hidden) return null;
  const t = FRAME_TIERS[tier];
  const mask = `radial-gradient(farthest-side, transparent ${HOLE}, #000 calc(${HOLE} + 0.5px))`;
  const rivetSize = BAND_OF_BOX * 0.42 * 100;
  const R = 50 - (BAND_OF_BOX * 100) / 2;
  const rivets = [];
  for (let i = 0; i < t.rivets; i++) {
    const a = (Math.PI * 2 * i) / t.rivets + Math.PI / t.rivets;
    rivets.push(
      <span
        key={i}
        style={{
          position: 'absolute',
          left: `${50 + R * Math.cos(a) - rivetSize / 2}%`,
          top: `${50 + R * Math.sin(a) - rivetSize / 2}%`,
          width: `${rivetSize}%`,
          height: `${rivetSize}%`,
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, #ffffff 0%, ${t.rivet} 55%, #3a2a10 100%)`,
          boxShadow: '0 0.5px 1px rgba(0,0,0,0.6)'
        }}
      />
    );
  }
  return (
    <div
      aria-hidden='true'
      data-frame-tier={tier}
      style={{
        position: 'absolute',
        inset: INSET,
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
      <div style={{ ...ring, background: t.ring, WebkitMask: mask, mask }} />
      <div
        style={{
          ...ring,
          boxShadow:
            'inset 0 1px 1px rgba(255,255,255,0.55), inset 0 -1px 2px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.35)'
        }}
      />
      {t.inlay && (
        <div
          style={{
            ...ring,
            inset: `${BAND_OF_BOX * 100 * 0.62}%`,
            border: `1.5px solid ${t.inlay}`,
            WebkitMask: mask,
            mask
          }}
        />
      )}
      {rivets}
    </div>
  );
}

export default memo(SeatFrame);
